import { useEffect, useState } from 'react'
import { apiGetFile } from '../../../../generic/Api_Functions'
import { CircularProgress, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useFullScreen } from '../useFullScreen'
// Extension lists + checkDocumentExtension come from the import-free leaf module (they
// used to be defined here / in SmartDocumentViewer, which tied light consumers to the
// heavy viewer graph). Re-exported below for backwards compatibility.
import {
  SmartBrowserDocumentViewerIframeExtensions,
  SmartBrowserDocumentViewerIframeExtensionsTypes,
  SmartBrowserDocumentViewerImageExtensions,
  SmartBrowserDocumentViewerVideoExtensions,
  SmartBrowserDocumentViewerExtensions,
  checkDocumentExtension
} from '../../documentExtensions'

const SmartBrowserDocumentViewer = ({ url, extension, documentName, requestFullScreen, convertedExtension, onLoadError }) => {
  const [blobUrl, setBlobUrl] = useState(null)
  const [error, setError] = useState(null)
  const { t_ } = useSmartTranslation()
  const fullScreenContainerRef = useFullScreen(requestFullScreen)

  useEffect(() => {
    let isCurrent = true // Flag to track if this effect instance is still valid
    let currentUrl = null

    const loadDocument = async () => {
      setError(null)
      setBlobUrl(null)
      try {
        const response = await apiGetFile(url)

        if (!response.ok) throw new Error('Failed to fetch document')

        let blob = await response.blob()
        if (!isCurrent) return

        if (extension && checkDocumentExtension(extension, SmartBrowserDocumentViewerIframeExtensions, convertedExtension)) {
          blob = new Blob([blob], { type: SmartBrowserDocumentViewerIframeExtensionsTypes[extension.toLowerCase()] })
        }

        currentUrl = URL.createObjectURL(blob)
        if (isCurrent) {
          setBlobUrl(currentUrl)
        } else {
          URL.revokeObjectURL(currentUrl) // Clean up if we finished too late
        }
      } catch (err) {
        if (isCurrent) {
          setError(err.message)
          // Historia 148 (S67): quien monta el visor puede querer distinguir «ya no existe» (404) de
          // un fallo de red, y reaccionar (avisar, recargar su lista). Opcional: sin el callback, el
          // comportamiento es exactamente el de antes.
          if (onLoadError) {
            onLoadError({ status: err.status, message: err.message })
          }
        }
      }
    }

    loadDocument()

    // Cleanup the URL to prevent memory leaks
    return () => {
      isCurrent = false
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [url, extension, convertedExtension, onLoadError])

  if (!extension) {
    return <Typography>{t_('Invalid extension')}</Typography>
  }
  if (error) return <div>{t_('Error: ') + error}</div>
  if (!blobUrl) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center'
      }}
      ><CircularProgress />
      </div>
    )
  }

  if (checkDocumentExtension(extension, SmartBrowserDocumentViewerImageExtensions, convertedExtension)) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          backgroundImage: `url(${blobUrl})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }}
        role='img'
        aria-label={documentName}
        ref={fullScreenContainerRef}
      />
    )
  }

  if (checkDocumentExtension(extension, SmartBrowserDocumentViewerVideoExtensions, convertedExtension)) {
    return (
      <video
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          maxHeight: '100%'
        }}
        src={blobUrl}
        controls
        ref={fullScreenContainerRef}
      />
    )
  }

  if (checkDocumentExtension(extension, SmartBrowserDocumentViewerIframeExtensions, convertedExtension)) {
    return (
      <iframe
        title={documentName}
        type={SmartBrowserDocumentViewerIframeExtensionsTypes[extension.toLowerCase()]}
        src={blobUrl}
        style={{ width: '100%', height: '100%' }}
        ref={fullScreenContainerRef}
        key={url}
      />
    )
  }
  return <Typography>{t_('Invalid extension')}</Typography>
}

export { SmartBrowserDocumentViewer, SmartBrowserDocumentViewerExtensions, SmartBrowserDocumentViewerImageExtensions }
